<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
			<WSubTitle text="Period shortcuts" />
			<span
				v-if="formulaSet"
				class="text-sm text-color-secondary"
				v-tooltip.top="'Hardcoded tariff formula set fingerprint'"
			>
				Formulas v{{ formulaSet.version }} · {{ formulaSet.checksum }}
			</span>
		</div>

		<div class="flex flex-wrap gap-2 mb-3">
			<PButton
				v-if="canGenerateInvoice"
				:disabled="!billingPeriodId || busy"
				:loading="busyKey === 'invoice'"
				icon="pi pi-file"
				label="Generate invoice"
				outlined
				rounded
				size="small"
				@click="$emit('generate-invoice')"
			/>
			<PButton
				v-if="canReports"
				:disabled="!billingPeriodId || busy"
				:loading="busyKey === 'invoice_package'"
				icon="pi pi-book"
				label="Invoice package"
				outlined
				rounded
				size="small"
				@click="$emit('report', 'invoice_package')"
			/>
			<PButton
				v-if="canReports"
				:disabled="!billingPeriodId || busy"
				:loading="busyKey === 'nee_export'"
				icon="pi pi-bolt"
				label="NEE export"
				outlined
				rounded
				size="small"
				@click="$emit('report', 'nee_export')"
			/>
			<PButton
				v-if="canReports"
				:disabled="!billingPeriodId || busy"
				:loading="busyKey === 'ppm'"
				icon="pi pi-chart-line"
				label="PPM report"
				outlined
				rounded
				size="small"
				@click="$emit('report', 'ppm')"
			/>
		</div>

		<div v-if="timeline?.length" class="mb-3">
			<div class="section-label mb-2">Period timeline</div>
			<ul class="timeline">
				<li v-for="(ev, idx) in timeline" :key="idx">
					<span class="text-color-secondary">{{ ev.at || '—' }}</span>
					— {{ ev.label }}
				</li>
			</ul>
		</div>

		<div v-if="chargeBreakdown?.length" class="mb-2">
			<div class="section-label mb-2">Charge breakdown</div>
			<div class="charge-bars">
				<div
					v-for="item in chargeBreakdown"
					:key="item.name"
					class="charge-row"
				>
					<span class="charge-name">{{ shortName(item.name) }}</span>
					<div class="bar-track">
						<div
							class="bar-fill"
							:style="{ width: barWidth(item.value) }"
						/>
					</div>
					<span class="charge-val">{{ fmt(item.value) }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'

	export default defineComponent({
		name: 'DashShortcuts',
		props: {
			billingPeriodId: { type: Number as PropType<number | null>, default: null },
			canGenerateInvoice: { type: Boolean, default: false },
			canReports: { type: Boolean, default: false },
			busy: { type: Boolean, default: false },
			busyKey: { type: String, default: '' },
			formulaSet: { type: Object as PropType<Record<string, any> | null>, default: null },
			timeline: {
				type: Array as PropType<Array<{ at: string | null; kind: string; label: string }>>,
				default: () => [],
			},
			chargeBreakdown: {
				type: Array as PropType<Array<{ name: string; value: number | null }>>,
				default: () => [],
			},
		},
		emits: ['generate-invoice', 'report'],
		computed: {
			maxCharge(): number {
				const vals = (this.chargeBreakdown || [])
					.map((c) => Math.abs(c.value || 0))
				return Math.max(1, ...vals)
			},
		},
		methods: {
			fmt(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })
			},
			shortName(name: string): string {
				return name
					.replace('CCRnCharges', 'CCR ')
					.replace('OMRnCharges', 'OMR ')
					.replace('NEEn', 'act')
					.replace('NEEd', 'deemed')
			},
			barWidth(v: number | null | undefined): string {
				if (v == null) return '0%'
				return `${Math.min(100, (Math.abs(v) / this.maxCharge) * 100)}%`
			},
		},
	})
</script>

<style scoped>
	.section-label {
		font-size: 0.85rem;
		font-weight: 600;
	}
	.timeline {
		margin: 0;
		padding-left: 1.1rem;
		font-size: 0.85rem;
	}
	.timeline li + li {
		margin-top: 0.2rem;
	}
	.charge-bars {
		display: flex;
		flex-direction: column;
		gap: 0.35rem;
	}
	.charge-row {
		display: grid;
		grid-template-columns: 7rem 1fr 4.5rem;
		gap: 0.5rem;
		align-items: center;
		font-size: 0.8rem;
	}
	.bar-track {
		height: 8px;
		background: var(--surface-border);
		border-radius: 4px;
		overflow: hidden;
	}
	.bar-fill {
		height: 100%;
		background: #2563eb;
		border-radius: 4px;
	}
	.charge-val {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}
</style>
